import React, { useEffect } from "react";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";

const MedicalTranscription = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <div className="bg-white-100 text-gray-800" style={{ marginTop: "80px" }}>
      <motion.header
        className="bg-blue-900 text-white py-16 text-center shadow-lg rounded-lg"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-100 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">
                  {" "}
                  🩺 Delivering Clarity, Enhancing{" "}
                </span>
                <span className="text-blue-600"> Patient Care</span>
              </h1>
              <p className="mt-4 font-bold">
                Professional Medical Transcription Services by Twenty Four Seven
                Consultancy
              </p>
              <p className="text-white mt-4">
                At Twenty Four Seven Consultancy, we turn voice recordings into
                precise, high-quality written documents—empowering healthcare
                providers to communicate efficiently and focus on patient care.
              </p>
              <p className="text-white mt-4 mb-8">
                📞 Get in Touch – Let’s Work Together!
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section
          id="transcription-services"
          className="py-24 bg-gradient-to-r from-blue-50 mt-4 to-white px-8 md:px-24"
          style={{ padding: "30px" }}
        >
          <div className="container mx-auto text-center bg-white shadow-xl rounded-2xl p-10">
            <h2 className="text-4xl font-extrabold text-blue-800 mb-4">
              🏥 Seamless Documentation for Streamlined Medical Workflows
            </h2>
            <p className="text-base text-gray-700 mb-6">
              Medical transcription doesn’t need to be complicated or costly.
              Our expert transcriptionists ensure:
            </p>
            <div className="grid gap-4 md:grid-cols-3 text-sm text-gray-700 text-left">
              <div className="bg-gray-100 p-4 rounded-xl">
                ✅ Highly accurate and timely documentation
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                🎯 Tailored solutions for your unique documentation needs
              </div>
              <div className="bg-gray-100 p-4 rounded-xl">
                💡 Cost-effective services without compromising quality
              </div>
            </div>
            <p className="text-sm text-gray-700 mt-4">
              Let us handle the paperwork so you can focus on healing lives.
            </p>
          </div>
        </section>
      </motion.div>

      {/* Why Choose Us? */}
      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section className="py-16 bg-gray-100">
          <h2
            className="text-3xl font-bold text-blue-900 text-center"
            data-aos="fade-up"
          >
            ✨ Why Choose Twenty Four Seven Consultancy?
          </h2>
          <div
            className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-8 mt-4"
            style={{ marginTop: "30px" }}
          >
            {[
              {
                title: "🔍 Unmatched Accuracy & Quality",
                description:
                  " Every transcription undergoes rigorous quality checks to ensure precision and consistency—mirroring your original voice notes down to the detail.",
              },
              {
                title: "💼 Deep Industry Expertise",
                description:
                  "We’re fluent in medical language, with years of experience across multiple specialties and understanding of the latest healthcare practices.",
              },
              {
                title: "🧩 Customized Transcription Solutions",
                description:
                  " Whether you need medical reports, patient histories, consultations, or research transcriptions—we tailor every service to meet your specific workflow.",
              },
              {
                title: "🔐 Advanced Data Security & Compliance",
                description:
                  " We maintain strict data protection standards, keeping all patient information confidential and HIPAA-compliant.",
              },
              {
                title: "⚡ Fast Turnaround Times",
                description:
                  "Our efficient team delivers documents quickly and accurately—so your practice never slows down.",
              },
              {
                title: "💰 Affordable and Scalable",
                description:
                  "Get premium service at competitive rates, helping you cut overhead while maintaining documentation excellence.",
              },
            ].map((service, index) => (
              <motion.div
                key={index}
                className="p-6 bg-gray-100 rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
                whileHover={{ scale: 1.05 }}
                data-aos="fade-up"
              >
                <h3 className="text-xl font-bold text-blue-900">
                  {service.title}
                </h3>
                <p className="mt-2">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </section>
      </motion.div>

      {/* Our Services */}
      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section className="py-16 bg-white-100 s-lg rounded-lg">
          <h2
            className="text-3xl font-bold text-blue-900 text-center"
            data-aos="fade-up"
          >
            📝 Our Comprehensive Medical Transcription Services
          </h2>
          <div
            className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-8 mt-4"
            style={{ marginTop: "30px" }}
          >
            {[
              {
                title: "🗂️ Medical Reports Transcription",
                description:
                  " From discharge summaries to diagnostic reports—we keep your patient records consistent and accessible.",
              },
              {
                title: "🩺 Consultation Transcription",
                description:
                  " Transform consultations into clear, reviewable documentation that enhances clinical decision-making.",
              },
              {
                title: "🔬 Medical Research Transcription",
                description:
                  " We transcribe interviews, seminars, and focus groups, supporting clinical researchers with clean, analysis-ready data.",
              },
              {
                title: "🎯 Custom Transcription Services",
                description:
                  " Have a unique use case? We’ll create a tailored solution that aligns with your internal workflows and compliance standards.",
              },
            ].map((service, index) => (
              <motion.div
                key={index}
                className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
                whileHover={{ scale: 1.05 }}
                data-aos="fade-up"
              >
                <h3 className="text-xl font-bold text-blue-900">
                  {service.title}
                </h3>
                <p className="mt-2">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </section>
      </motion.div>
      {/* Inbound Call Management Section */}
      <motion.div
        className="p-6 bg-gray-100 rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section className="py-16 bg-blue-50 px-6">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center">
            {/* Left Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:w-1/2 mb-10 lg:mb-0 lg:pr-12"
              data-aos="fade-up"
            >
              <h2 className="text-3xl font-bold text-gray-900">
                🤝 Partner with Twenty{" "}
                <span className="text-blue-500">Four Seven Consultancy</span>
              </h2>
              <ul className="mt-6 space-y-4" style={{ marginTop: "30px" }}>
                {[
                  {
                    title:
                      "✔️ A reliable transcription partner with proven industry expertise",
                    description:
                      "Our professional team ensures patient inquiries are handled efficiently, improving satisfaction and trust.",
                  },
                  {
                    title:
                      " ✔️ Enhanced compliance and confidentiality for sensitive patient data",
                    description:
                      "Seamless appointment bookings, letting your medical staff focus on providing quality care.",
                  },
                  {
                    title: " ✔️ Speedy turnaround times to match your schedule",
                    description:
                      "We address all patient concerns professionally, strengthening the provider-patient relationship.",
                  },
                  {
                    title:
                      "✔️ Scalable, flexible transcription services aligned to your practice’s needs",
                    description:
                      "Streamline prescription management with our prompt and accurate refill services.",
                  },
                ].map((service, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    className="flex bg-white p-4 shadow-md rounded-lg"
                    data-aos="fade-up"
                  >
                    <div className="w-10 h-10 bg-blue-500 flex items-center justify-center rounded-full mr-4">
                      <span className="text-lg font-bold">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-blue-600 mt-2">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {/* {service.description} */}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </ul>
              <h5 className="mt-4 font-bold">
                📩 Contact us today to streamline your documentation process and
                elevate your patient care delivery.
              </h5>
            </motion.div>
          </div>
        </section>
      </motion.div>
      {/* Call to Action */}
      <motion.section
        className="py-16 text-center bg-white-100 shadow-md rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <h2 className="text-3xl font-bold text-blue-900">
          🚀 Ready to Start Your Project?
        </h2>
        <h5 className="mt-4 font-bolds">
          🔗 Let’s Start Together — Your journey toward accurate, secure, and
          efficient transcription services begins now.
        </h5>
      </motion.section>
    </div>
  );
};

export default MedicalTranscription;
